import { useState } from "react";
import { BriefcaseBusiness, Pencil } from "lucide-react";
import { JobSearchStatus } from "@api/shared-runtime/enums";
import JobPreferencesForm from "@/components/Forms/JobPreferencesForm";
import { PROFILE_FORM_STYLES } from "@/components/Forms/profile-form-styles";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import type { RouterOutputs } from "@/trpc";

type JobPreferences = RouterOutputs["profile"]["getJobPreferences"];

type ProfileJobPreferencesProps = {
  userId: string;
  preferences: JobPreferences | undefined;
};

const ANY_CITY = "ANY_CITY";
const rateFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function PreferenceDetail({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1.5">
      <dt className="text-xs font-medium tracking-wide text-slate-500 uppercase">
        {label}
      </dt>
      <dd className="text-sm leading-6 whitespace-pre-line text-slate-700">
        {value}
      </dd>
    </div>
  );
}

function getLocationPreference(
  preferences: NonNullable<JobPreferences>,
): string {
  const cities = preferences.cities
    .filter((city) => city.name !== ANY_CITY)
    .map((city) => city.name);
  const openToOtherCities = preferences.cities.some(
    (city) => city.name === ANY_CITY,
  );
  if (cities.length === 0) {
    return openToOtherCities ? "Open to any location" : "No locations set";
  }

  const locations = cities.join(" · ");
  if (openToOtherCities) {
    return `${locations}\nAlso open to other locations`;
  }
  return locations;
}

function PreferenceSummary({
  preferences,
}: {
  preferences: JobPreferences | undefined;
}) {
  if (!preferences) {
    return (
      <div className="space-y-3">
        <div className="flex size-12 items-center justify-center rounded-xl bg-primary/5 text-primary">
          <BriefcaseBusiness aria-hidden="true" className="size-5" />
        </div>
        <p className="font-semibold text-slate-900">Your next opportunity</p>
        <p className="text-sm leading-relaxed text-slate-500">
          Add your preferred settings, locations, and availability to help find
          work that fits.
        </p>
      </div>
    );
  }

  if (preferences.searchStatusId === JobSearchStatus.NotLooking) {
    return (
      <div className="space-y-2">
        <p className="font-semibold text-slate-900">Not currently looking</p>
        <p className="text-sm leading-relaxed text-slate-500">
          Update your preferences when you’re ready to explore new
          opportunities.
        </p>
      </div>
    );
  }

  const hasMinimumRate = preferences.minHourlyRate != null;

  return (
    <dl className="space-y-6">
      <div className="space-y-2">
        <dt className="text-xs font-medium tracking-wide text-slate-500 uppercase">
          Min. hourly rate
        </dt>
        <dd className="text-3xl font-semibold tracking-tight text-slate-900">
          {hasMinimumRate ? (
            <>
              {rateFormatter.format(Number(preferences.minHourlyRate))}
              <span className="ml-1 text-sm font-medium tracking-normal text-slate-500">
                /hr
              </span>
            </>
          ) : (
            <span className="text-xl">No minimum set</span>
          )}
        </dd>
      </div>
      <div className="space-y-5 border-t border-slate-200 pt-5">
        <PreferenceDetail
          label="Practice setting"
          value={
            preferences.practiceSettings
              .map((setting) => setting.displayText)
              .join(" · ") || "Any practice setting"
          }
        />
        <PreferenceDetail
          label="Employment type"
          value={
            preferences.employmentTypes
              .map((type) => type.displayText)
              .join(" · ") || "Any employment type"
          }
        />
        <PreferenceDetail
          label="Availability"
          value={preferences.availabilityDescription || "Not specified"}
        />
        <PreferenceDetail
          label="Preferred locations"
          value={getLocationPreference(preferences)}
        />
      </div>
    </dl>
  );
}

export function ProfileJobPreferences({
  userId,
  preferences,
}: ProfileJobPreferencesProps) {
  const [editing, setEditing] = useState(false);

  return (
    <section aria-labelledby="profile-job-preferences-heading">
      <Dialog open={editing} onOpenChange={setEditing}>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
          <div className="mb-5 flex items-center justify-between gap-3">
            <h2
              id="profile-job-preferences-heading"
              className="min-w-0 flex-1 text-base font-semibold text-slate-900"
            >
              Job preferences
            </h2>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                className="shrink-0 gap-2 px-3 text-sm font-semibold text-primary hover:bg-primary/5 hover:text-primary"
              >
                <Pencil aria-hidden="true" className="size-4" />
                Edit
                <span className="sr-only"> job preferences</span>
              </Button>
            </DialogTrigger>
          </div>
          <PreferenceSummary preferences={preferences} />
        </div>
        <DialogContent className={PROFILE_FORM_STYLES.dialog}>
          <DialogHeader className={PROFILE_FORM_STYLES.header}>
            <div className="flex items-start gap-4">
              <div className={PROFILE_FORM_STYLES.icon}>
                <BriefcaseBusiness aria-hidden="true" className="size-5" />
              </div>
              <div className="min-w-0 space-y-1.5">
                <p className={PROFILE_FORM_STYLES.eyebrow}>Your profile</p>
                <DialogTitle className={PROFILE_FORM_STYLES.title}>
                  Job preferences
                </DialogTitle>
                <DialogDescription className={PROFILE_FORM_STYLES.description}>
                  Let us know what you’re looking for in your next opportunity.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <JobPreferencesForm
            appearance="profile"
            userId={userId}
            onComplete={() => setEditing(false)}
            onCancel={() => setEditing(false)}
          />
        </DialogContent>
      </Dialog>
    </section>
  );
}
